/**
 * The voter's "Advertising partners" choice from Your Privacy Choices.
 *
 * Opt-out: advertising partners (ad pixels, HubSpot ad audiences) are on until
 * the voter turns them off, so a missing record ("not decided") is treated as
 * on and only `granted: false` stops them. Kept byte-identical with the web
 * copy so the stored key and JSON shape match on both clients.
 */

export const AD_CONSENT_STORAGE_KEY = 'vm_ad_consent';

export type AdConsentSource = 'banner' | 'settings' | 'gpc';

/** Device record, stored as JSON under `AD_CONSENT_STORAGE_KEY`. */
export interface AdConsent {
  granted: boolean;
  source: AdConsentSource;
  /** Device-clock time of the last change; informational, never compared with the server's. */
  updatedAt: string;
  /** A change made on this device that the account has not confirmed yet. */
  pending: boolean;
  /** The account record's server-stamped `updated_at` this device last matched. */
  syncedAt: string | null;
}

/** Account record, as `/auth/callback` and `GET /user/settings` return it. */
export interface AccountAdConsent {
  granted: boolean;
  updated_at?: string | null;
  source?: string | null;
  version?: string | null;
}

const SOURCES: readonly AdConsentSource[] = ['banner', 'settings', 'gpc'];

const asSource = (value: unknown): AdConsentSource =>
  SOURCES.includes(value as AdConsentSource) ? (value as AdConsentSource) : 'settings';

/** A choice made on this device, pending until the account confirms it. */
export function buildAdConsent(
  granted: boolean,
  source: AdConsentSource,
  previous?: AdConsent | null
): AdConsent {
  return {
    granted,
    source,
    updatedAt: new Date().toISOString(),
    pending: true,
    syncedAt: previous?.syncedAt ?? null,
  };
}

/** The account's record as this device stores it once the two agree. */
export function adConsentFromAccount(account: AccountAdConsent): AdConsent {
  return {
    granted: account.granted,
    source: asSource(account.source),
    updatedAt: account.updated_at ?? '',
    pending: false,
    syncedAt: account.updated_at ?? null,
  };
}

export function parseAdConsent(raw: string | null): AdConsent | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<AdConsent>;
    if (typeof value?.granted !== 'boolean') return null;
    return {
      granted: value.granted,
      source: asSource(value.source),
      updatedAt: typeof value.updatedAt === 'string' ? value.updatedAt : '',
      // A record without the flag predates syncing; treat it as unconfirmed.
      pending: typeof value.pending === 'boolean' ? value.pending : true,
      syncedAt: typeof value.syncedAt === 'string' ? value.syncedAt : null,
    };
  } catch {
    return null;
  }
}

export type AdConsentUpload = { granted: boolean; source: AdConsentSource };

/**
 * Settle this device against the account at sign-in and on Settings load.
 *
 * Device and server clocks are never compared, since a skewed device clock
 * could make an old choice look newer. Instead the device tracks whether it
 * holds an unconfirmed change, and which account record it last matched:
 *
 * - Nothing unconfirmed here: the account is current, so the device adopts it.
 * - An unconfirmed change here, and the account is still the record this
 *   device last matched: the change is newer, so it is uploaded.
 * - Both changed since they last matched (or never matched): the opt-out
 *   wins, the choice that cannot share data the voter declined to share.
 */
export function reconcileAdConsent(
  device: AdConsent | null,
  account: AccountAdConsent | null | undefined
): { applyToDevice?: AdConsent; uploadToAccount?: AdConsentUpload } {
  const upload = (record: AdConsent) => ({
    uploadToAccount: { granted: record.granted, source: record.source },
  });
  const hasAccount = account != null && typeof account.granted === 'boolean';
  if (!hasAccount) return device ? upload(device) : {};

  const fromAccount = adConsentFromAccount(account!);
  if (!device) return { applyToDevice: fromAccount };

  if (device.granted === fromAccount.granted) {
    // Agreed. Record which account version that was, unless already recorded.
    const recorded = !device.pending && device.syncedAt === fromAccount.syncedAt;
    return recorded ? {} : { applyToDevice: fromAccount };
  }
  if (!device.pending) return { applyToDevice: fromAccount };

  const accountUnchanged = device.syncedAt !== null && device.syncedAt === fromAccount.syncedAt;
  if (accountUnchanged || !device.granted) return upload(device);
  return { applyToDevice: fromAccount };
}
